import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';

import {
  getIsInvite,
  getToken,
  getRefreshToken,
  logOut,
  onRefreshToken,
  getIsLoggedInOnBehalf,
  getLoggedInOnBehalfWorkspaceId,
} from '../auth/slice';

import { getWorkspaceId } from '../workspace/slice';
import { getConfigValue } from '@zonofi/common/functions/get-config-value/get-config-value';
import { RootState } from '../root/root-types';
import { UserTokens } from '../auth/model';

import { requestFinished, requestStarted } from '../api-inspector/slice';

export const REDUCER_PATH_ZONO_SELLER_API = 'zonoSellerApi';

let serverToken: string | null = null;

/**
 * Standard baseQuery
 */
const baseQuery = fetchBaseQuery({
  baseUrl: getConfigValue('API_ENDPOINT'),

  prepareHeaders: (headers, { getState }) => {
    const accessToken = getToken(getState() as RootState);

    if (!headers.has('authorization') && accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }

    return headers;
  },
});

/**
 * Fetch server token
 */
async function getServerToken(state: RootState) {
  if (serverToken) return serverToken;

  const userToken = getToken(state);
  const workspaceId = getWorkspaceId(state);

  const res = await fetch(
    `${getConfigValue(
      'API_ENDPOINT'
    )}hub/getServerToken?sellerWorkspaceId=${workspaceId}`,
    {
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    }
  );

  const data = await res.json();

  serverToken = data?.token;

  return serverToken;
}

export const baseQueryWithRouter: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  any,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const start = performance.now();

  const requestId = crypto.randomUUID();

  const state = api.getState() as RootState;

  const url = typeof args === 'string' ? args : args.url;

  const isLocalApi = typeof url === 'string' && url.includes('localhost');

  let modifiedArgs = args;

  /**
   * LOCAL BACKEND
   */
  // if (isLocalApi) {
  //   const token = await getServerToken(state);

  //   if (typeof args === 'string') {
  //     modifiedArgs = {
  //       url: args,
  //       headers: {
  //         authorization: `Bearer ${token}`,
  //       },
  //     };
  //   } else {
  //     modifiedArgs = {
  //       ...args,
  //       headers: {
  //         ...args.headers,
  //         authorization: `Bearer ${token}`,
  //       },
  //     };
  //   }
  // }

  const requestInfo =
    typeof modifiedArgs === 'string'
      ? {
          url: modifiedArgs,
          method: 'GET',
        }
      : modifiedArgs;

  /**
   * API INSPECTOR START
   */
  api.dispatch(
    requestStarted({
      id: requestId,
      endpoint: api.endpoint,
      url: requestInfo.url,
      method: requestInfo.method || 'GET',
      request: requestInfo.body,
      status: 'pending',
      route: window.location.pathname,
      timestamp: Date.now(),
    })
  );

  let result = await baseQuery(modifiedArgs, api, extraOptions);

  const refreshToken = getRefreshToken(state);
  const accessToken = getToken(state);
  const isInvite = getIsInvite(state);
  const workspaceId = getWorkspaceId(state);
  const isLoggedInOnBehalf = getIsLoggedInOnBehalf(state);

  const loggedInOnBehalfWorkspaceId = getLoggedInOnBehalfWorkspaceId(state);

  const shouldAttemptRefresh =
    !isLocalApi &&
    !isInvite &&
    (result.error?.status === 401 ||
      (result.error?.status === 'PARSING_ERROR' &&
        result.error?.originalStatus === 401) ||
      (isLoggedInOnBehalf && result.error?.status === 403)) &&
    !['sendOTP', 'verifyOTP'].includes(api.endpoint);

  if (shouldAttemptRefresh) {
    const refreshResult = await baseQuery(
      {
        url: isLoggedInOnBehalf
          ? 'regenerate/supportToken'
          : 'regenerate/token',

        method: 'POST',

        body: {
          refreshToken: refreshToken,
          token: accessToken,

          workspaceId: isLoggedInOnBehalf
            ? loggedInOnBehalfWorkspaceId
            : workspaceId,
        },
      },
      api,
      extraOptions
    );

    if (refreshResult.data) {
      const tokens = refreshResult.data as UserTokens;

      api.dispatch(onRefreshToken(tokens));

      result = await baseQuery(modifiedArgs, api, extraOptions);
    } else {
      api.dispatch(logOut());
    }
  }

  /**
   * API INSPECTOR END
   */
  api.dispatch(
    requestFinished({
      id: requestId,
      response: result.data,
      error: result.error,
      duration: performance.now() - start,
    })
  );

  return result;
};

export const Api = createApi({
  reducerPath: REDUCER_PATH_ZONO_SELLER_API,

  baseQuery: baseQueryWithRouter,

  endpoints: () => ({}),

  tagTypes: [
    'User',
    'Payment',
    'ActiveOrder',
    'TeamInvites',
    'Notifications',
    'CartSummary',
    'workspace',
    'userData',
    'UpdatePoFile',
    'cfaList',
    'divsionList',
    'teamMemberList',
    'sellerAction',
    'trip-stock',
    'trip-clockout',
    'wabaUserData',
    'userViewPreference',
    'FileList',
    'departmentList',
    'AgentSettings',
    'Connectors',
  ],
});
